import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, UserRound, Loader2 } from "lucide-react";
import { APP_NAME, LOGO } from "@/lib/brand";
import { t } from "@/lib/i18n";

export default function Onboarding() {
  const { user, loading, completeOnboarding, logout } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("company");
  const [companyName, setCompanyName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading) {
      if (!user) navigate("/login", { replace: true });
      else if (!user.needs_onboarding) navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await completeOnboarding({
        role,
        company_name: role === "company" ? companyName : undefined,
        invite_code: role === "agent" ? inviteCode : undefined,
      });
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const Card = ({ value, icon: Icon, title, desc }) => (
    <button
      type="button"
      data-testid={`onboarding-role-${value}`}
      onClick={() => setRole(value)}
      className={`flex-1 text-left rounded-2xl border p-5 transition-all ${
        role === value ? "border-primary bg-primary/15" : "border-border bg-card hover:border-primary/40"
      }`}
    >
      <Icon className={`h-6 w-6 mb-3 ${role === value ? "text-primary" : "text-muted-foreground"}`} />
      <p className="font-semibold">{title}</p>
      <p className="text-xs text-muted-foreground mt-1">{desc}</p>
    </button>
  );

  return (
    <div className="min-h-screen app-soft-bg flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg bg-card/80 backdrop-blur border border-border rounded-3xl p-8"
      >
        <div className="flex items-center gap-2 mb-6">
          <img src={LOGO} alt={APP_NAME} className="h-9 w-9 rounded-lg" />
          <span className="font-display text-lg font-extrabold">{APP_NAME}</span>
        </div>
        <h1 className="font-display text-2xl font-extrabold mb-1">{t("Completa il profilo")}</h1>
        <p className="text-sm text-muted-foreground mb-6">
          {t("Ciao")} {user?.name || ""}{t(", dicci come userai la piattaforma.")}
        </p>

        <form onSubmit={submit} className="space-y-5">
          <div className="flex gap-3">
            <Card value="company" icon={Building2} title={t("Sono un'azienda")} desc="Gestisco prodotti e agenti" />
            <Card value="agent" icon={UserRound} title={t("Sono un agente")} desc="Prendo ordini per un'azienda" />
          </div>

          {role === "company" ? (
            <div className="space-y-2">
              <Label>{t("Nome azienda")}</Label>
              <Input data-testid="onboarding-company-input" required value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder={t("Acme S.r.l.")} />
            </div>
          ) : (
            <div className="space-y-2">
              <Label>{t("Codice invito azienda")}</Label>
              <Input data-testid="onboarding-invite-input" required value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} placeholder={t("ACME-1A2B3C")} className="font-mono-j uppercase" />
            </div>
          )}

          <Button data-testid="onboarding-submit-btn" type="submit" disabled={busy} className="w-full h-11 font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Continua")}
          </Button>
          <button type="button" onClick={logout} className="w-full text-xs text-muted-foreground hover:text-foreground">
            {t("Esci")}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
